import React, {lazy, Suspense} from 'react';
import {Outlet, RouteObject, createBrowserRouter} from 'react-router-dom';

import {AuthGuard} from '../AuthGuard';
import {App} from '../App';
import {WaitForUser} from 'WaitForUser';
import {ContextManager, NoAuthContextManager} from 'contexts';
import {PageEvents} from 'shared/components/Analytics/PageEvents';
import {routes, SuspenseLoader} from 'routes/Routes';
import {
    Accounts,
    Login,
    LoginLayout,
    NoAuth,
    PasswordReset,
    RegisterForm,
    RegisterSuccess,
    RequireAuth,
    SetPassword,
} from 'components';
import {Loader} from 'shared/helpers';
import TokenHandler from 'components/customer/Auth/TokenHandler';
import RoomPlannerPdf from 'components/customer/RoomPlannerPDF/RoomPlannerPdf';
import EventDispatcher from 'shared/components/EventDispatcher';
import {Flex} from 'shared/components/StyledComponents';
import {SetPasswordSuccess} from 'components/customer/Auth/Registration/SetPasswordSuccess';
import {RoomPlannerProvider} from 'components/customer/RoomPlanner/components/RoomPlannerContext';
import RoomPlannerDashboard from 'components/customer/RoomPlanner/components/RoomPlannerDashboard';
import {MobileRestriction} from 'shared/components/MobileRestriction';
import Layout from 'shared/components/Layout';

const TDLDPreview = lazy(
    () => import('components/customer/LayoutDesigner/FullscreenTDLDPreview')
);

const BTMPreview = lazy(
    () => import('components/customer/BTM/Preview/FullscreenPreview')
);

const StandalonePreview3D = lazy(
    () => import('components/customer/Preview3D/StandalonePreview3DWrapper')
);

const JobDetailsPDF = lazy(
    () => import('components/customer/PDF/JobDetailsPDF')
);

const InvoicePDF = lazy(
    () => import('components/customer/Confirmation/Invoice')
);

export const routerConfigs: RouteObject[] = [
    {
        path: '/v2/*',
        element: <Outlet />,
        children: [
            {
                path: 'btm-preview',
                element: (
                    <WaitForUser allowRedirect={false}>
                        <PageEvents>
                            <Suspense fallback={<Loader loader={true} />}>
                                <BTMPreview />
                            </Suspense>
                        </PageEvents>
                    </WaitForUser>
                ),
            },
            {
                path: 'tdld-preview',
                element: (
                    <WaitForUser allowRedirect={false}>
                        <PageEvents>
                            <ContextManager>
                                <Suspense fallback={<Loader loader={true} />}>
                                    <TDLDPreview />
                                </Suspense>
                            </ContextManager>
                        </PageEvents>
                    </WaitForUser>
                ),
            },
            {
                path: 'preview-3drp/:roomSlug',
                element: (
                    <NoAuth>
                        <NoAuthContextManager>
                            <RoomPlannerProvider isRoomPreview>
                                <section className="job-page mainContainer">
                                    <Layout>
                                        <SuspenseLoader>
                                            <MobileRestriction
                                                onlyShowMessage={false}
                                                message="Room planner works best on larger screens. Please maximize your window, reduce your browser zoom, or use a desktop, laptop or tablet.">
                                                <RoomPlannerDashboard />
                                            </MobileRestriction>
                                        </SuspenseLoader>
                                    </Layout>
                                </section>
                            </RoomPlannerProvider>
                        </NoAuthContextManager>
                    </NoAuth>
                ),
                errorElement: (
                    <EventDispatcher events={['pageRendered', 'pageFailed']}>
                        <Flex
                            data-cy="error-boundary"
                            style={{fontSize: 60, fontWeight: 'bold'}}
                            $justifyContent="center"
                            $alignItems="center"
                            $height="100vh">
                            Unable to load 3D preview
                        </Flex>
                    </EventDispatcher>
                ),
            },
            {
                path: 'preview-3d/:slug',
                element: (
                    <PageEvents>
                        <Suspense fallback={<Loader loader={true} />}>
                            <StandalonePreview3D />
                        </Suspense>
                    </PageEvents>
                ),
                errorElement: (
                    <EventDispatcher events={['pageRendered', 'pageFailed']}>
                        <Flex
                            data-cy="error-boundary"
                            style={{fontSize: 60, fontWeight: 'bold'}}
                            $justifyContent="center"
                            $alignItems="center"
                            $height="100vh">
                            Unable to load 3D preview
                        </Flex>
                    </EventDispatcher>
                ),
            },
            {
                path: 'pdf/preview/:jobId',
                element: (
                    <WaitForUser allowRedirect={false}>
                        <PageEvents>
                            <Suspense fallback={<Loader loader={true} />}>
                                <JobDetailsPDF />
                            </Suspense>
                        </PageEvents>
                    </WaitForUser>
                ),
            },
            {
                path: 'pdf/invoice/:jobId',
                element: (
                    <WaitForUser allowRedirect={false}>
                        <PageEvents>
                            <Suspense fallback={<Loader loader={true} />}>
                                <InvoicePDF />
                            </Suspense>
                        </PageEvents>
                    </WaitForUser>
                ),
            },
            {
                path: 'pdf/3d-room-planner/job/:jobId/room/:roomId/report',
                element: (
                    <WaitForUser allowRedirect={false}>
                        <PageEvents>
                            <Suspense fallback={<Loader loader={true} />}>
                                <RoomPlannerPdf />
                            </Suspense>
                        </PageEvents>
                    </WaitForUser>
                ),
            },
            {
                path: '*',
                element: (
                    <WaitForUser>
                        <AuthGuard />
                    </WaitForUser>
                ),
                children: [
                    {
                        path: 'login',
                        element: (
                            <NoAuth>
                                <LoginLayout element={<Login />} />
                            </NoAuth>
                        ),
                    },
                    {
                        path: 'customer-token',
                        element: (
                            <NoAuth>
                                <TokenHandler />
                            </NoAuth>
                        ),
                    },
                    {
                        path: 'reset-password',
                        element: (
                            <NoAuth>
                                <LoginLayout element={<PasswordReset />} />
                            </NoAuth>
                        ),
                    },
                    {
                        path: 'set-password',
                        element: (
                            <NoAuth>
                                <LoginLayout element={<SetPassword />} />
                            </NoAuth>
                        ),
                    },
                    {
                        path: 'set-password-success',
                        element: (
                            <NoAuth>
                                <LoginLayout
                                    element={<SetPasswordSuccess />}
                                    bodyWidth="700px"
                                    fullBackground
                                />
                            </NoAuth>
                        ),
                    },
                    {
                        path: 'register',
                        element: (
                            <NoAuth>
                                <LoginLayout
                                    element={<RegisterForm />}
                                    bodyWidth="700px"
                                    fullBackground
                                    noBackgroundMobile
                                />
                            </NoAuth>
                        ),
                    },
                    {
                        path: 'register-success',
                        element: (
                            <NoAuth>
                                <LoginLayout
                                    element={<RegisterSuccess />}
                                    bodyWidth="700px"
                                    fullBackground
                                />
                            </NoAuth>
                        ),
                    },
                    {
                        path: 'accounts',
                        element: (
                            <NoAuth>
                                <Accounts />
                            </NoAuth>
                        ),
                    },
                    {
                        path: '*',
                        element: (
                            <RequireAuth>
                                <PageEvents>
                                    <App />
                                </PageEvents>
                            </RequireAuth>
                        ),
                        children: routes,
                    },
                ],
            },
        ],
    },
];

export const router = createBrowserRouter(routerConfigs);
